import { appRequest } from "routes";
import * as XLSX from "xlsx";

export const exportToExcel = (data: any, sendMail?: any, setFileData?: any) => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, "0");
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const year = today.getFullYear();
  const hours = String(today.getHours()).padStart(2, "0");
  const minutes = String(today.getMinutes()).padStart(2, "0");

  const name = `DRUG_ORDER_${day}_${month}_${year}-${hours}_${minutes}`;
  let workbook: any = null;
  workbook = data;

  let excelFile: any = null;
  if (sendMail.includes("mail")) {
    const base64 = XLSX.write(workbook, {
      type: "base64",
      bookType: "xlsx",
    });
    const base64Prefix = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64}`;
    setFileData(base64Prefix);
    excelFile = base64Prefix;
  }
  return excelFile;
};

const handleSubmit = async (
  fieldName: any,
  fileData: any,
  toastNotify?: any
) => {
  if (fieldName.length <= 0) {
    toastNotify({
      title: `No email found.`,
      message: "Please update.",
      type: "warning",
    });
    return;
  }
  try {
    toastNotify({
      title: `Sending Mail.`,
      message: "Please wait for a few seconds...",
      type: "info",
    });

    const response: any = await appRequest("document", "sendMail", {
      fieldName,
      fileData,
    });

    if (response && response.status === "success") {
      toastNotify({
        title: `Success`,
        message: "Mail Sent Successfully",
        type: "success",
      });
    } else {
      toastNotify({
        title: `Mail not sent.`,
        message: response?.message || "Something went wrong.",
        type: "error",
      });
    }
  } catch (error) {
    toastNotify({
      title: `Error sending mail.`,
      message: "Something went wrong.",
      type: "error",
    });
  }
};

export const handleDownloadExcel = async (
  transformedData: any,
  values: any,
  toastNotify?: any,
  sendMail?: any,
  fieldName?: any,
  fileData?: any,
  setFileData?: any,
  setFinalResults?: any
) => {
  const excelFile = await exportToExcel(transformedData, sendMail, setFileData);

  if (sendMail.includes("mail") && excelFile !== null) {
    handleSubmit(fieldName, excelFile, toastNotify);
  }
};

export function updateValues(data: any): any {
  if (Array.isArray(data)) {
    return data.map((item) => {
      if (
        typeof item === "object" &&
        item !== null &&
        "originalValues" in item &&
        "editedValues" in item
      ) {
        if (item.editedValues !== null) {
          item.originalValues = item.editedValues;
        }
      } else {
        return updateValues(item);
      }
      return item;
    });
  } else if (typeof data === "object" && data !== null) {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        if (
          typeof data[key] === "object" &&
          data[key] !== null &&
          "originalValues" in data[key] &&
          "editedValues" in data[key]
        ) {
          if (data[key].editedValues !== null) {
            data[key].originalValues = data[key].editedValues;
          }
        } else if (Array.isArray(data[key])) {
          data[key] = updateValues(data[key]);
        } else {
          data[key] = updateValues(data[key]);
        }
      }
    }
  }
  return data;
}

type DynamicObject = any;

const getDynamicHeaders = (data: any[]): string[] => {
  const allHeaders: Set<string> = new Set();

  data.forEach((page: any) => {
    if (page.fields) {
      const fieldArray = Object.values(page.fields).find((value) =>
        Array.isArray(value)
      );
      const fieldData = Array.isArray(fieldArray) ? fieldArray : [page.fields]; // Ensure array

      fieldData.forEach((item: any) => {
        Object.keys(item || {}).forEach((field: string) => {
          allHeaders.add(field);
        });
      });
    }
  });

  return Array.from(allHeaders);
};

const prepareSheetData = (data: any[], headers: string[]): any[][] => {
  const sheetData: any[][] = [];

  data.forEach((page: any) => {
    if (page.fields) {
      // Detect if fields has an array inside; otherwise, treat fields as a flat object
      const fieldArray = Object.values(page.fields).find((value) =>
        Array.isArray(value)
      );
      const fieldData = Array.isArray(fieldArray) ? fieldArray : [page.fields]; // Ensure array

      fieldData.forEach((item: any) => {
        const rowData = headers.map((header: string) => {
          return (
            item[header]?.editedValues ||
            item[header]?.originalValues ||
            item[header] ||
            ""
          );
        });
        sheetData.push(rowData);
      });
    }
  });

  return sheetData;
};

const createWorkbook = (data: any[]): XLSX.WorkBook => {
  const wb: XLSX.WorkBook = XLSX.utils.book_new();

  data.forEach((page: any) => {
    const originalHeaders: string[] = getDynamicHeaders([page]);
    const originalSheetData: any[][] = prepareSheetData(
      [page],
      originalHeaders
    );

    const headerReplacements: Record<string, string> = {
      name: "DRUG_NAME",
      dosage: "DRUG_CODE",
      quantity: "DRUG_QUANTITY",
      UNIT_PRICE: "UNIT_PRICE",
      buy_X: "BUY_X",
      get_Y: "GET_Y",
    };

    const headers = originalHeaders.map(
      (header) => headerReplacements[header] || header
    );

    const sheetData = originalSheetData.map((row) =>
      originalHeaders.map((header) => row[originalHeaders.indexOf(header)])
    );

    // const additionalHeaders = ["UNIT_PRICE", "BUY_X", "GET_Y"];
    // additionalHeaders.forEach((header) => {
    //   if (!headers.includes(header)) {
    //     headers.push(header);
    //   }
    // });

    const worksheetData = [headers, ...sheetData];
    const sheetName: string = `Page ${page.page || "1"}`;

    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(worksheetData);

    headers.forEach((_, colIdx) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIdx });
      if (!ws[cellAddress]) ws[cellAddress] = { t: "s", v: headers[colIdx] };
      ws[cellAddress].s = { font: { bold: true } };
    });

    const colWidths = headers.map((header, colIdx) => {
      const maxContentWidth = Math.max(
        header.length,
        ...sheetData.map((row) =>
          row[colIdx] ? row[colIdx].toString().length : 0
        )
      );
      return { wch: maxContentWidth + 2 };
    });
    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  return wb;
};

const exportToExcelss = (data: any[], fileName: string, sendMail: any) => {
  const wb = createWorkbook(data);

  if (sendMail.includes("download")) {
    XLSX.writeFile(wb, fileName);
  }
  return wb;
};

const nameMapping: { [key: string]: string } = {
  slNo : "SL. NO.",
  name: "DRUG_NAME",
  dosage: "DRUG_CODE",
  quantity: "DRUG_QUANTITY",
  UNIT_PRICE: "UNIT_PRICE",
  buy_X: "BUY_X",
  get_Y: "GET_Y",
};

// Add "Sl. No." at the beginning of the desired order
const columnOrder = ["slNo", "name", "UNIT_PRICE", "quantity", "buy_X", "get_Y"];

const exportToExcelsss = (data: any[], isDownload?: boolean) => {
  const workbook = XLSX.utils.book_new();

  data.forEach((page: any) => {
    const rows: any[] = [];
    const fields = page?.fields;

    if (fields && typeof fields === "object") {
      Object.keys(fields).forEach((fieldKey) => {
        const fieldItems = fields[fieldKey];

        if (Array.isArray(fieldItems)) {
          fieldItems.forEach((item: any, index: number) => {
            const row: any = {};
            columnOrder.forEach((key) => {
              if (key === "slNo") {
                row["slNo"] = index + 1; // Add the serial number
              } else {
                const mappedKey = nameMapping[key] ?? key;
                row[mappedKey] =
  item[key]?.editedValues != null ? item[key]?.editedValues : item[key]?.originalValues ?? "";

              }
            });
            rows.push(row);
          });
        }
      });
    }

    // Create the worksheet
    const worksheet = XLSX.utils.json_to_sheet(rows);

    // Define header row styling
    const headerStyle = {
      fill: { fgColor: { rgb: "90EE90" } }, // Light green background
      font: { bold: true, color: { rgb: "000000" } }, // Bold black font
      alignment: { horizontal: "center", vertical: "center" }, // Center alignment for all headers
    };

    // Apply the style to the first row (header row)
    const headerRow = columnOrder.map((key) => nameMapping[key] ?? key);
    headerRow.forEach((col, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
      const cell = worksheet[cellRef];
      if (cell) {
        cell.v = col; // Set cell value
        cell.s = headerStyle; // Apply style
      }
    });

    // Adjust column widths
    const columnWidths = columnOrder.map((key) => {
      const mappedKey = key === "slNo" ? "slNo" : nameMapping[key] ?? key;
      const maxContentLength = Math.max(
        mappedKey.length,
        ...rows.map((row) =>
          row[mappedKey] ? row[mappedKey].toString().length : 0
        )
      );
      return { wch: maxContentLength + 2 };
    });
    worksheet["!cols"] = columnWidths;

    // Append worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, `Page_${page.pageIndex + 1}`);
  });

  // Generate file name
  const today = new Date();
  const fileName = `DRUG_ORDER_${today.toISOString().slice(0, 19).replace(/:/g, "_")}`;

  // Download or return the workbook
  if (isDownload) {
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  } else {
    return workbook;
  }
};













export default exportToExcelsss;

export const transformData = (pages: any[]) => {
  if (!pages || pages.length === 0 || !pages[0].fields) {
    console.error("Invalid pages data or missing 'fields' property");
    return [];
  }

  return pages
    .map((page: any, pageIndex: number) => {
      return Object.entries(page.fields || {}).map(([fieldName, fieldData]) => {
        // Ensure fieldData is always an array
        const dataArray = Array.isArray(fieldData)
          ? fieldData
          : fieldData && typeof fieldData === "object"
          ? [fieldData]
          : [];

        if (dataArray.length > 0) {
          const rows = dataArray.map((item: any, index: number) => {
            const transformedRow: any = { id: index };

            // Ensure default values for missing fields
            const fieldsToCheck = ["name", "dosage", "UNIT_PRICE", "quantity", "buy_X", "get_Y"];
            fieldsToCheck.forEach((field) => {
              if (!(field in item)) {
                item[field] = null; // Set missing fields to null
              }
            });

            for (const [key, value] of Object.entries(item)) {
              if (key === "name" && "dosage" in item) {
                transformedRow[key] = {
                  originalValues: `${value} ${item.dosage || ""}`.trim(),
                  editedValues: null,
                };
              } else if (key === "dosage") {
                transformedRow[key] = {
                  originalValues: "",
                  editedValues: null,
                };
              } else if (key === "quantity" && typeof value === "string") {
                const cleanedValue = value
                  .replace(/\b[a-zA-Z]*[yY][a-zA-Z]*\b/g, "")
                  .replace(/[^\d*+\-/().\sxX]/g, "")
                  .replace(/[xX]+$/g, "");

                transformedRow[key] = {
                  originalValues: cleanedValue.trim(),
                  editedValues: null,
                };
              } else {
                transformedRow[key] = {
                  originalValues: value,
                  editedValues: null,
                };
              }
            }

            // Ensure 'UNIT_PRICE', 'buy_X', and 'get_Y' are included, even if they are missing
            ["UNIT_PRICE", "buy_X", "get_Y"].forEach((newField) => {
              transformedRow[newField] = {
                originalValues: transformedRow[newField]?.originalValues ?? null,
                editedValues: transformedRow[newField]?.editedValues ?? null,
              };
            });

            return transformedRow;
          });

          return { fieldName, rows, pageIndex };
        }
        return null;
      });
    })
    .flat()
    .filter(Boolean);
};


// Save the changes (this will only log data for now)
export const handleSaveData = (data: any) => {
  // Group data back by pageIndex and fieldName
  const groupedData = data.reduce(
    (acc: any[], { fieldName, rows, pageIndex }: any) => {
      if (!acc[pageIndex]) {
        acc[pageIndex] = { pageIndex, fields: {} };
      }
      acc[pageIndex].fields[fieldName] = rows.map((row: any) => {
        const rowData: any = {};
        for (const key in row) {
          if (key !== "id") {
            // Set dosage to an empty string
            rowData[key] = key === "dosage" ? "" : row[key];
          }
        }
        return rowData;
      });
      return acc;
    },
    []
  );

  return groupedData;
};





// export const exportExcel = (excelData:any) => {
//   if (!Array.isArray(excelData) || excelData.length === 0) return;

//   const allFields:any = [];
//   const allTableRows:any = [];

//   excelData.forEach((item, index) => {
//     const fields = item?.values?.values?.fields || {};
//     const flatFields:any = { DocumentIndex: index + 1 };

//     Object.entries(fields).forEach(([key, value]) => {
//       flatFields[key] = typeof value === 'object' ? JSON.stringify(value) : value;
//     });

//     allFields.push(flatFields);

//     const tableRows = item?.values?.values?.tables?.Data_for_posting || [];
//     tableRows.forEach((row:any) => {
//       const flatRow :any= { DocumentIndex: index + 1 };
//       Object.entries(row).forEach(([key, value]) => {
//         flatRow[key] = typeof value === 'object' ? JSON.stringify(value) : value;
//       });
//       allTableRows.push(flatRow);
//     });
//   });

//   // Export FIELDS
//   const fieldsSheet = XLSX.utils.json_to_sheet(allFields);
//   fieldsSheet['!cols'] = autoFitColumns(allFields);

//   const fieldsWorkbook = XLSX.utils.book_new();
//   XLSX.utils.book_append_sheet(fieldsWorkbook, fieldsSheet, 'Fields');
//   XLSX.writeFile(fieldsWorkbook, 'OCR_data.xlsx');

//   // Export TABLES
//   if (allTableRows.length > 0) {
//     const tableSheet = XLSX.utils.json_to_sheet(allTableRows);
//     tableSheet['!cols'] = autoFitColumns(allTableRows);

//     const tableWorkbook = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(tableWorkbook, tableSheet, 'Data_for_posting');
//     XLSX.writeFile(tableWorkbook, 'Data_for_posting.xlsx');
//   }
// };

// Auto-fit column widths

export const exportExcel = (excelData: any) => {
  if (!Array.isArray(excelData) || excelData.length === 0) return;

  const allFields: any[] = [];
  const allTableRows: any[] = [];

  const excludeFields = ['api_id', 'file_name', 'start_timestamp', 'end_timestamp', 'file_path'];
  const excludeTableFields = ['order'];

  excelData.forEach((item, index) => {
    const fields = item?.values?.values?.fields || {};
    const flatFields: any = { DocumentIndex: index + 1 };

    Object.entries(fields).forEach(([key, value]) => {
      if (!excludeFields.includes(key)) {
        flatFields[key] = typeof value === 'object' ? JSON.stringify(value) : value;
      }
    });

    allFields.push(flatFields);

    const tableRows = item?.values?.values?.tables?.Data_for_posting || [];
    tableRows.forEach((row: any) => {
      const flatRow: any = { DocumentIndex: index + 1 };
      Object.entries(row).forEach(([key, value]) => {
        if (!excludeTableFields.includes(key)) {
          flatRow[key] = typeof value === 'object' ? JSON.stringify(value) : value;
        }
      });
      allTableRows.push(flatRow);
    });
  });

  // Export FIELDS
  const fieldsSheet = XLSX.utils.json_to_sheet(allFields);
  fieldsSheet['!cols'] = autoFitColumns(allFields);

  const fieldsWorkbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(fieldsWorkbook, fieldsSheet, 'Fields');
  XLSX.writeFile(fieldsWorkbook, 'OCR_data.xlsx');

  // Export TABLES
  if (allTableRows.length > 0) {
    const tableSheet = XLSX.utils.json_to_sheet(allTableRows);
    tableSheet['!cols'] = autoFitColumns(allTableRows);

    const tableWorkbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(tableWorkbook, tableSheet, 'Data_for_posting');
    XLSX.writeFile(tableWorkbook, 'Data_for_posting.xlsx');
  }
};

// export const exportAllExcel = (excelData: any) => {
//   if (!Array.isArray(excelData) || excelData.length === 0) return;

//   const allFields: any[] = [];

//   const excludeFields = ['api_id', 'file_name', 'start_timestamp', 'end_timestamp', 'file_path'];

//   excelData.forEach((item, index) => {
//     const fields = item?.values?.values?.fields || {};
//     const flatFields: any = { DocumentIndex: index + 1 };

//     Object.entries(fields).forEach(([key, value]) => {
//       if (!excludeFields.includes(key)) {
//         flatFields[key] = typeof value === 'object' ? JSON.stringify(value) : value;
//       }
//     });

//     allFields.push(flatFields);
//   });

//   // Export FIELDS only
//   const fieldsSheet = XLSX.utils.json_to_sheet(allFields);
//   fieldsSheet['!cols'] = autoFitColumns(allFields);

//   const fieldsWorkbook = XLSX.utils.book_new();
//   XLSX.utils.book_append_sheet(fieldsWorkbook, fieldsSheet, 'Fields');
//   XLSX.writeFile(fieldsWorkbook, 'OCR_data.xlsx');
// };

export const exportAllExcel = (excelData: any) => {
  if (!Array.isArray(excelData) || excelData.length === 0) return;

  const allFields: any[] = [];

  const excludeFields = ['api_id', 'file_name', 'start_timestamp', 'end_timestamp', 'file_path'];

  excelData.forEach((item, index) => {
    const fields = item?.values?.values?.fields || {};
    const flatFields: any = { DocumentIndex: index + 1 };

    // Add all necessary fields, excluding unwanted ones
    Object.entries(fields).forEach(([key, value]) => {
      if (!excludeFields.includes(key)) {
        flatFields[key] = typeof value === 'object' ? JSON.stringify(value) : value;
      }
    });

    // Add `type` column based on the condition
    const status = item?.status;
    const extractionType = item?.tags?.extraction?.type;

    if (status === 'passed') {
      flatFields.type = 'posted';
    } else if (status === 'pending') {
      flatFields.type = extractionType === 'manual' ? 'exception' : 'pending';
    } else {
      flatFields.type = 'unknown'; // fallback case
    }

    allFields.push(flatFields);
  });

  // Export FIELDS only
  const fieldsSheet = XLSX.utils.json_to_sheet(allFields);
  fieldsSheet['!cols'] = autoFitColumns(allFields);

  const fieldsWorkbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(fieldsWorkbook, fieldsSheet, 'Fields');
  XLSX.writeFile(fieldsWorkbook, 'OCR_data.xlsx');
};


const autoFitColumns = (data:any) => {
  const keys = Object.keys(data[0] || {});
  return keys.map(key => ({
    wch: Math.max(
      key.length,
      ...data.map((row:any) => String(row[key] || '').length)
    ) + 5
  }));
};






















